import { useState, useEffect } from 'react';
import { fetchSsoConfig, initSso } from '../../api/ssoApi.js';
import AppLogo from '../shared/AppLogo.jsx';
import { getDomainBranding } from '../../tenant/tenantConfig.js';
import MfaVerifyStep from './MfaVerifyStep.jsx';
import MfaSetupModal from './MfaSetupModal.jsx';
import TestEnvBadge from './TestEnvBadge.jsx';
import {
  callMfaRecoveryRequest,
  callMfaRecoveryVerify,
  callPasswordResetMagicLink,
  callPasswordResetVerify,
} from '../../api/mfaApi.js';
import { getFunctionUrl } from '../../api/functionUrl.js';

/**
 * Microsoft logo SVG for the SSO button.
 */
function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * CredentialFlow
 *
 * Tenant-agnostic credential + MFA flow component.
 *
 * Handles: password form, passwordless, MFA verify, MFA setup, grace notice,
 * temp-password-expired, password-voided, MFA recovery request/sent.
 *
 * When authentication fully succeeds (post-MFA where required), calls
 * onComplete(token, user). Does NOT establish the session itself — the
 * caller decides what to do with the token (slug: store in localStorage;
 * universal login: mint a handoff code and redirect).
 *
 * Props:
 *   tenantId        string    — tenant slug / ID (required)
 *   tenant          object?   — { branding: { companyName, logoImage } }
 *   initialUsername string?   — pre-fills the username field
 *   autoPasswordless bool?    — attempt passwordless immediately with initialUsername
 *   recoveryToken   string?   — ?mfaRecovery= URL param (if landing on recovery link)
 *   functionsBaseUrl string?  — env-specific Function App base URL. When set (a
 *                     beta/test tenant during central login) every auth call —
 *                     tenant-login, MFA, SSO, recovery — targets that env's FA.
 *                     Omit for the current build-time environment.
 *   environment     string?   — 'production' | 'test'; drives the TEST badge only
 *   onComplete      fn        — (token: string, user: object) => void
 *   onBack          fn?       — called when user clicks back on the first screen
 */
function CredentialFlow({
  tenantId,
  tenant,
  initialUsername = '',
  autoPasswordless = false,
  recoveryToken = null,
  setupResetToken = null,
  functionsBaseUrl,
  environment = 'production',
  onComplete,
  onBack,
}) {
  const platformName = tenant?.branding?.companyName?.trim() || getDomainBranding().name;
  const logoSrc = tenant?.branding?.logoImage || null;

  // ── Form state ────────────────────────────────────────────────────────────
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── Screen state ──────────────────────────────────────────────────────────
  const [screen, setScreen] = useState(() => {
    if (recoveryToken) return 'mfa-recovery-verifying';
    if (setupResetToken) return 'setup-reset-verifying';
    if (autoPasswordless && initialUsername) return 'passwordless-checking';
    return 'form';
  });

  const [mfaData, setMfaData] = useState(null); // { method, challengeToken, totpTag }
  const [graceData, setGraceData] = useState(null); // { token, user, loginsRemaining, challengeToken? }
  const [setupData, setSetupData] = useState(null); // { challengeToken, reason, fromGrace? }

  // Recovery form state
  const [recoveryUsername, setRecoveryUsername] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  // Password reset state (self-service for temp-password-expired)
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  // Magic link state (send-setup-link path)
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState('');

  // ── SSO config ────────────────────────────────────────────────────────────
  const [ssoMode, setSsoMode] = useState('disabled');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(true);
  const ssoEnabled = ssoMode === 'optional' || ssoMode === 'enforced';

  useEffect(() => {
    if (tenantId) {
      fetchSsoConfig(tenantId, functionsBaseUrl).then((cfg) => setSsoMode(cfg.mode || 'disabled'));
    }
  }, [tenantId, functionsBaseUrl]);

  useEffect(() => {
    if (ssoMode === 'enforced') setShowPasswordForm(false);
  }, [ssoMode]);

  // ── Auto-passwordless on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!autoPasswordless || !initialUsername) return;

    callLogin(initialUsername.trim(), null, { passwordless: true })
      .then((result) => {
        if (result.status === 'mfa-challenge') {
          setMfaData({
            method: result.method,
            challengeToken: result.challengeToken,
            totpTag: result.totpTag || null,
          });
          setScreen('mfa-verify');
        } else if (result.status === 'mfa-setup-required') {
          setSetupData({
            challengeToken: result.challengeToken,
            reason: result.reason,
            fromGrace: false,
          });
          setScreen('mfa-setup');
        } else if (result.status === 'mfa-recovery-required') {
          setRecoveryUsername(initialUsername.trim());
          setRecoveryError('');
          setScreen('mfa-recovery-form');
        } else {
          // Not enrolled — fall back to password form
          setScreen('form');
        }
      })
      .catch(() => setScreen('form'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── MFA recovery link verification ───────────────────────────────────────
  useEffect(() => {
    if (!recoveryToken) return;

    callMfaRecoveryVerify(recoveryToken, functionsBaseUrl)
      .then((result) => {
        if (result.status === 'mfa-setup-required' && result.challengeToken) {
          setSetupData({
            challengeToken: result.challengeToken,
            reason: result.reason || 'device-recovery',
            fromGrace: false,
          });
          setScreen('mfa-setup');
        } else {
          setRecoveryError(result.error || 'Recovery link is invalid or has already been used.');
          setScreen('mfa-recovery-form');
        }
      })
      .catch(() => {
        setRecoveryError('Recovery link is invalid or has already been used.');
        setScreen('mfa-recovery-form');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Password-reset magic link verification ───────────────────────────────
  useEffect(() => {
    if (!setupResetToken) return;

    callPasswordResetVerify(tenantId, setupResetToken, functionsBaseUrl)
      .then((result) => {
        if (result.status === 'mfa-setup-required' && result.challengeToken) {
          setSetupData({
            challengeToken: result.challengeToken,
            reason: result.reason || 'password-reset',
            fromGrace: false,
          });
          setScreen('mfa-setup');
        } else {
          setResetError(result.error || 'Setup link is invalid or has already been used.');
          setScreen('temp-password-expired');
        }
      })
      .catch(() => {
        setResetError('Setup link is invalid or has already been used.');
        setScreen('temp-password-expired');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Internal login call (calls API directly — no useAuth dependency) ──────
  async function callLogin(uname, pwd, options = {}) {
    try {
      const loginUrl = functionsBaseUrl
        ? `${functionsBaseUrl}/api/tenant-login`
        : getFunctionUrl('tenant-login');
      const body = { tenantId, username: uname };
      if (options.passwordless) {
        body.passwordless = true;
      } else {
        body.password = pwd;
      }
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data.passwordVoided) return { status: 'password-voided' };
        return { status: 'error', error: data.error || 'Incorrect username or password.' };
      }

      const { status } = data;
      if (status === 'ok') return { status: 'ok', token: data.token, user: data.user };
      if (status === 'grace-period') {
        return {
          status: 'grace-period',
          token: data.token,
          user: data.user,
          loginsRemaining: data.loginsRemaining,
          challengeToken: data.challengeToken || null,
        };
      }
      if (status === 'mfa-challenge')
        return {
          status: 'mfa-challenge',
          method: data.method,
          challengeToken: data.challengeToken,
          // Names the live authenticator entry; absent for pre-tag enrolments.
          totpTag: data.totpTag || null,
        };
      if (status === 'mfa-setup-required')
        return {
          status: 'mfa-setup-required',
          challengeToken: data.challengeToken,
          reason: data.reason,
        };
      if (status === 'mfa-recovery-required') return { status: 'mfa-recovery-required' };
      if (status === 'temp-password-expired') return { status: 'temp-password-expired' };
      return { status: 'error', error: 'Unexpected response from server.' };
    } catch (e) {
      console.error('[CredentialFlow] login error:', e);
      return { status: 'error', error: 'Network error. Please try again.' };
    }
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  // Send a stranded account (password voided, no authenticator enrolled) into
  // the email-recovery flow with the username pre-filled.
  const goToRecovery = () => {
    setRecoveryUsername(username.trim());
    setRecoveryError('');
    setScreen('mfa-recovery-form');
  };

  const handleLogin = async () => {
    if (!username.trim() || !password) {
      setError('Please enter username and password.');
      return;
    }
    setLoading(true);
    setError('');

    const result = await callLogin(username.trim(), password);
    setLoading(false);

    switch (result.status) {
      case 'ok':
        onComplete(result.token, result.user);
        break;

      case 'grace-period':
        setGraceData({
          token: result.token,
          user: result.user,
          loginsRemaining: result.loginsRemaining,
          challengeToken: result.challengeToken || null,
        });
        setScreen('grace-notice');
        break;

      case 'mfa-challenge':
        setMfaData({
          method: result.method,
          challengeToken: result.challengeToken,
          totpTag: result.totpTag || null,
        });
        setScreen('mfa-verify');
        break;

      case 'mfa-setup-required':
        setSetupData({
          challengeToken: result.challengeToken,
          reason: result.reason,
          fromGrace: false,
        });
        setScreen('mfa-setup');
        break;

      case 'temp-password-expired':
        setScreen('temp-password-expired');
        break;

      case 'password-voided':
        setScreen('passwordless-checking');
        handlePasswordlessLogin();
        break;

      case 'mfa-recovery-required':
        // Stranded account: password voided but no authenticator enrolled.
        // Route to the email-recovery flow — re-enrolment must be proven via
        // the recovery email, never re-opened from a username alone.
        goToRecovery();
        break;

      default:
        setError(result.error || 'Incorrect username or password.');
    }
  };

  const handlePasswordlessLogin = async () => {
    setLoading(true);
    setError('');

    const result = await callLogin(username.trim(), null, { passwordless: true });
    setLoading(false);

    if (result.status === 'mfa-challenge') {
      setMfaData({
        method: result.method,
        challengeToken: result.challengeToken,
        totpTag: result.totpTag || null,
      });
      setScreen('mfa-verify');
    } else if (result.status === 'mfa-recovery-required') {
      goToRecovery();
    } else if (result.status === 'error') {
      setError(result.error || 'Could not start authenticator sign-in. Please try again.');
      setScreen('password-voided');
    } else {
      setError('Unexpected response. Please try again.');
      setScreen('password-voided');
    }
  };

  const handlePasswordReset = async () => {
    setResetLoading(true);
    setResetError('');
    try {
      const baseUrl = functionsBaseUrl || '';
      const res = await fetch(`${baseUrl}/api/password-reset-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, username: username.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setScreen('password-reset-sent');
    } catch {
      setResetError('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleMagicLinkRequest = async () => {
    setMagicLinkLoading(true);
    setMagicLinkError('');
    const result = await callPasswordResetMagicLink(tenantId, username.trim(), functionsBaseUrl);
    setMagicLinkLoading(false);
    if (result?.error) {
      setMagicLinkError(result.error);
      return;
    }
    setScreen('magic-link-sent');
  };

  const handleRecoveryRequest = async () => {
    if (!recoveryUsername.trim()) {
      setRecoveryError('Please enter your username or email.');
      return;
    }
    setRecoveryLoading(true);
    setRecoveryError('');
    const result = await callMfaRecoveryRequest(
      tenantId,
      recoveryUsername.trim(),
      functionsBaseUrl
    );
    setRecoveryLoading(false);
    if (result?.error) {
      setRecoveryError(result.error);
      return;
    }
    setScreen('mfa-recovery-sent');
  };

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    setError('');
    try {
      const result = await initSso(tenantId, functionsBaseUrl);
      if (result.error) {
        setError(result.error);
        setSsoLoading(false);
        return;
      }
      sessionStorage.setItem('sso_state', result.state);
      sessionStorage.setItem('sso_tenant_id', tenantId);
      window.location.href = result.authUrl;
    } catch {
      setError('Failed to connect to Microsoft. Please try again.');
      setSsoLoading(false);
    }
  };

  // ── Shared layout wrappers ────────────────────────────────────────────────

  const outer = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    fontFamily: 'var(--font-body)',
  };
  const inner = { width: '100%', maxWidth: 400, padding: '0 24px' };
  const monoLabel = {
    display: 'block',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--fg3)',
    fontFamily: 'var(--font-mono)',
    marginBottom: 6,
  };

  function Header() {
    return (
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <AppLogo size={52} borderRadius={14} style={{ margin: '0 auto 16px' }} src={logoSrc} />
        <div className="display" style={{ fontSize: 26, fontWeight: 600, color: 'var(--fg)' }}>
          {platformName}
        </div>
        {environment === 'test' && <TestEnvBadge />}
        <div
          style={{
            fontSize: 12,
            color: 'var(--fg3)',
            marginTop: 4,
            fontFamily: 'var(--font-mono)',
          }}
        >
          Compliance Management Platform
        </div>
      </div>
    );
  }

  // ── Passwordless checking ─────────────────────────────────────────────────

  if (screen === 'passwordless-checking') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div style={{ textAlign: 'center', color: 'var(--fg3)', fontSize: 13 }}>
            Checking your account…
          </div>
        </div>
      </div>
    );
  }

  // ── MFA verify ────────────────────────────────────────────────────────────

  if (screen === 'mfa-verify' && mfaData) {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <MfaVerifyStep
              method={mfaData.method}
              challengeToken={mfaData.challengeToken}
              totpTag={mfaData.totpTag}
              functionsBaseUrl={functionsBaseUrl}
              onRecovery={goToRecovery}
              onSuccess={(token, user) => onComplete(token, user)}
              onBack={() => {
                setScreen('form');
                setError('');
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Grace-period notice ───────────────────────────────────────────────────

  if (screen === 'grace-notice' && graceData) {
    const { token, user, loginsRemaining, challengeToken: graceChallengeToken } = graceData;
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 6, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Set up multi-factor authentication
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                background: 'var(--warn-bg, rgba(245,158,11,0.08))',
                border: '1px solid rgba(245,158,11,0.25)',
                borderRadius: 8,
                padding: '12px 14px',
                margin: '16px 0',
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1, marginTop: 1 }}>⚠️</span>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--warn)',
                    marginBottom: 4,
                  }}
                >
                  {loginsRemaining === 1
                    ? 'This is your last grace login'
                    : `${loginsRemaining} grace login${loginsRemaining !== 1 ? 's' : ''} remaining`}
                </div>
                <div style={{ fontSize: 12, color: 'var(--fg2)', lineHeight: 1.5 }}>
                  After your grace logins are used, you will need an authenticator app to sign in.
                  Set up MFA now to avoid being locked out.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 0', fontSize: 13 }}
                onClick={() => {
                  if (graceChallengeToken) {
                    setSetupData({
                      challengeToken: graceChallengeToken,
                      reason: 'grace',
                      fromGrace: true,
                    });
                    setScreen('mfa-setup');
                  } else {
                    onComplete(token, user);
                  }
                }}
              >
                Set up authenticator
              </button>
              <button
                className="btn"
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  padding: '10px 0',
                  fontSize: 13,
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  color: 'var(--fg2)',
                }}
                onClick={() => onComplete(token, user)}
              >
                Continue to app
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── MFA setup wizard ──────────────────────────────────────────────────────

  if (screen === 'mfa-setup' && setupData) {
    const { challengeToken: setupChallengeToken, fromGrace } = setupData;
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <MfaSetupModal
              challengeToken={setupChallengeToken}
              functionsBaseUrl={functionsBaseUrl}
              onComplete={(token, user) => onComplete(token, user)}
              onBack={() => {
                if (fromGrace) {
                  setScreen('grace-notice');
                } else {
                  setScreen('form');
                  setError('');
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── MFA setup required (intermediate) ────────────────────────────────────

  if (screen === 'mfa-setup-required') {
    const reason = setupData?.reason;
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 16, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Authenticator required
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                background: 'rgba(239,68,68,0.07)',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 20,
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1, marginTop: 1 }}>🔒</span>
              <div style={{ fontSize: 12, color: 'var(--fg2)', lineHeight: 1.5 }}>
                {reason === 'new-user'
                  ? 'Your account requires MFA setup before you can access your workspace.'
                  : 'Your grace period has ended. Set up an authenticator to regain access.'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 0', fontSize: 13 }}
                onClick={() => setScreen('mfa-setup')}
              >
                Set up authenticator
              </button>
              <button
                className="btn"
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  padding: '10px 0',
                  fontSize: 13,
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  color: 'var(--fg2)',
                }}
                onClick={() => {
                  setScreen('form');
                  setError('');
                }}
              >
                ← Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Temporary password expired ────────────────────────────────────────────

  if (screen === 'temp-password-expired') {
    const anyError = magicLinkError || resetError;
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Temporary password expired
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg2)', lineHeight: 1.5, marginBottom: 20 }}>
              Your temporary password expired before you completed setup. Send yourself a setup link
              to skip the password entirely and go straight to authenticator registration.
            </div>
            {anyError && (
              <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>
                {anyError}
              </div>
            )}
            <button
              className="btn btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '11px 0',
                fontSize: 13,
                marginBottom: 10,
              }}
              onClick={handleMagicLinkRequest}
              disabled={magicLinkLoading || resetLoading}
            >
              {magicLinkLoading ? 'Sending…' : 'Send me a setup link'}
            </button>
            <button
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '10px 0',
                fontSize: 13,
                marginBottom: 10,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                color: 'var(--fg3)',
              }}
              onClick={handlePasswordReset}
              disabled={resetLoading || magicLinkLoading}
            >
              {resetLoading ? 'Sending…' : 'Send me a new temporary password instead'}
            </button>
            <button
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '10px 0',
                fontSize: 13,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                color: 'var(--fg2)',
              }}
              onClick={() => {
                setScreen('form');
                setError('');
                setResetError('');
                setMagicLinkError('');
              }}
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'password-reset-sent') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Check your email
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg2)', lineHeight: 1.5, marginBottom: 20 }}>
              If an account exists for <strong style={{ color: 'var(--fg)' }}>{username}</strong>, a
              new temporary password has been sent to the email address on file. It expires in 3
              days.
            </div>
            <button
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '10px 0',
                fontSize: 13,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                color: 'var(--fg2)',
              }}
              onClick={() => {
                setScreen('form');
                setError('');
              }}
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'magic-link-sent') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Check your email
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg2)', lineHeight: 1.5, marginBottom: 20 }}>
              If an account exists for <strong style={{ color: 'var(--fg)' }}>{username}</strong>, a
              setup link has been sent to the email address on file. Click the link in the email to
              register your authenticator — no password needed.
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg3)',
                lineHeight: 1.5,
                marginBottom: 20,
                padding: '10px 12px',
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 8,
              }}
            >
              The link expires in 60 minutes and can only be used once.
            </div>
            <button
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '10px 0',
                fontSize: 13,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                color: 'var(--fg2)',
              }}
              onClick={() => {
                setScreen('form');
                setError('');
              }}
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Password voided ───────────────────────────────────────────────────────

  if (screen === 'password-voided') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 16, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Sign in with your authenticator
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg2)', lineHeight: 1.5, marginBottom: 20 }}>
              Password login is disabled for{' '}
              <strong style={{ color: 'var(--fg)' }}>{username}</strong>. Sign in with your
              registered authenticator (passkey, fingerprint, or authenticator app).
            </div>

            {error && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: 'var(--danger-bg)',
                  border: '1px solid rgba(251,113,133,0.25)',
                  color: 'var(--danger)',
                  fontSize: 12,
                  marginBottom: 16,
                }}
              >
                {error}
              </div>
            )}

            <button
              className="btn btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '11px 0',
                fontSize: 13,
                marginBottom: 8,
              }}
              onClick={handlePasswordlessLogin}
              disabled={loading}
            >
              {loading ? 'Starting…' : 'Sign in with authenticator'}
            </button>
            <button
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '10px 0',
                fontSize: 13,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                color: 'var(--fg2)',
              }}
              onClick={() => {
                setScreen('form');
                setError('');
                setPassword('');
              }}
            >
              ← Back
            </button>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  setRecoveryUsername(username.trim());
                  setRecoveryError('');
                  setScreen('mfa-recovery-form');
                }}
                style={{
                  fontSize: 12,
                  color: 'var(--fg3)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                  padding: 0,
                  textDecoration: 'underline',
                  textDecorationColor: 'var(--border)',
                }}
              >
                Lost your authenticator?
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Setup-reset link verifying ────────────────────────────────────────────

  if (screen === 'setup-reset-verifying') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div style={{ textAlign: 'center', color: 'var(--fg3)', fontSize: 13 }}>
            Verifying setup link…
          </div>
        </div>
      </div>
    );
  }

  // ── MFA recovery verifying ────────────────────────────────────────────────

  if (screen === 'mfa-recovery-verifying') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div style={{ textAlign: 'center', color: 'var(--fg3)', fontSize: 13 }}>
            Verifying recovery link…
          </div>
        </div>
      </div>
    );
  }

  // ── MFA recovery request form ─────────────────────────────────────────────

  if (screen === 'mfa-recovery-form') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Reset your authenticator
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg2)', lineHeight: 1.6, marginBottom: 20 }}>
              Enter your username or email address. If your account has MFA enabled, we&apos;ll send
              you a one-time recovery link valid for 15 minutes.
            </div>

            {recoveryError && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: 'var(--danger-bg)',
                  border: '1px solid rgba(251,113,133,0.25)',
                  color: 'var(--danger)',
                  fontSize: 12,
                  marginBottom: 16,
                }}
              >
                {recoveryError}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={monoLabel}>Username or email</label>
              <input
                value={recoveryUsername}
                onChange={(e) => setRecoveryUsername(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRecoveryRequest()}
                className="input"
                placeholder="Enter your username or email"
                autoFocus
              />
            </div>

            <button
              className="btn btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '11px 0',
                fontSize: 13,
                marginBottom: 8,
              }}
              onClick={handleRecoveryRequest}
              disabled={recoveryLoading}
            >
              {recoveryLoading ? 'Sending…' : 'Send recovery link'}
            </button>
            <button
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '10px 0',
                fontSize: 13,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                color: 'var(--fg2)',
              }}
              onClick={() => {
                setScreen('form');
                setRecoveryError('');
                setRecoveryUsername('');
              }}
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── MFA recovery sent ─────────────────────────────────────────────────────

  if (screen === 'mfa-recovery-sent') {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Check your email
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg2)', lineHeight: 1.6, marginBottom: 20 }}>
              If your account has MFA enabled, a recovery link has been sent to your registered
              email address. The link expires in 15 minutes and can only be used once.
            </div>
            <button
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '10px 0',
                fontSize: 13,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                color: 'var(--fg2)',
              }}
              onClick={() => {
                setScreen('form');
                setRecoveryError('');
                setRecoveryUsername('');
              }}
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Standard login form ───────────────────────────────────────────────────

  return (
    <div style={outer}>
      <div style={inner}>
        <Header />
        <div className="card" style={{ padding: 32 }}>
          <div style={{ marginBottom: 24, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
            Sign in to your workspace
          </div>

          {ssoEnabled && (
            <>
              <button
                onClick={handleSsoLogin}
                disabled={ssoLoading}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: '11px 0',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: ssoLoading ? 'wait' : 'pointer',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--bg3)',
                  color: 'var(--fg)',
                  transition: 'all 0.15s',
                  fontFamily: 'var(--font-body)',
                }}
              >
                <MicrosoftLogo />
                {ssoLoading ? 'Redirecting to Microsoft…' : 'Sign in with Microsoft'}
              </button>

              {showPasswordForm && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--fg3)',
                      fontFamily: 'var(--font-mono)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    or
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
              )}
            </>
          )}

          {showPasswordForm && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={monoLabel}>Username</label>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  className="input"
                  placeholder="Enter your username"
                  autoFocus={!ssoEnabled}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={monoLabel}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  className="input"
                  placeholder="Enter your password"
                />
              </div>

              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 0', fontSize: 13 }}
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>

              <div
                style={{
                  marginTop: 10,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <button
                  type="button"
                  onClick={handlePasswordlessLogin}
                  disabled={!username.trim() || loading}
                  style={{
                    fontSize: 12,
                    color: 'var(--fg3)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono)',
                    padding: 0,
                    textDecoration: 'underline',
                    textDecorationColor: 'var(--border)',
                  }}
                >
                  Sign in with authenticator instead
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRecoveryUsername(username.trim());
                    setRecoveryError('');
                    setScreen('mfa-recovery-form');
                  }}
                  style={{
                    fontSize: 12,
                    color: 'var(--fg3)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono)',
                    padding: 0,
                    textDecoration: 'underline',
                    textDecorationColor: 'var(--border)',
                  }}
                >
                  Lost your authenticator?
                </button>
              </div>
            </>
          )}

          {error && (
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                background: 'var(--danger-bg)',
                border: '1px solid rgba(251,113,133,0.25)',
                color: 'var(--danger)',
                fontSize: 12,
                marginTop: 16,
              }}
            >
              {error}
            </div>
          )}

          {ssoMode === 'enforced' && !showPasswordForm && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                onClick={() => setShowPasswordForm(true)}
                style={{
                  fontSize: 11,
                  color: 'var(--fg3)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                  textDecoration: 'underline',
                  textDecorationColor: 'var(--border)',
                }}
              >
                Use password instead (fallback users only)
              </button>
            </div>
          )}

          <div
            style={{
              marginTop: 20,
              padding: '12px 14px',
              borderRadius: 8,
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              fontSize: 11,
              color: 'var(--fg3)',
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            {ssoEnabled
              ? 'Sign in with your Microsoft account or contact your administrator'
              : 'Contact your administrator for credentials'}
          </div>

          {onBack && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                type="button"
                onClick={onBack}
                style={{
                  fontSize: 12,
                  color: 'var(--fg3)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                  padding: 0,
                }}
              >
                ← Use a different account
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CredentialFlow;
